import React, { useState, useEffect, useCallback } from 'react';
import './WarningLettersPipeline.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BASE = `${API_URL}/api/v1/admin/warning-letters`;

/* ====================================================================
   Shared: BatchControls
   ==================================================================== */
function BatchControls({ label, limit, setLimit, onRun, running, extra }) {
  return (
    <div className="batch-controls">
      <span className="batch-label">{label}</span>
      {extra}
      <label>
        Batch size:
        <input type="number" min={1} max={100} value={limit}
          onChange={e => setLimit(Math.max(1, +e.target.value))} />
      </label>
      <button onClick={onRun} disabled={running} className="btn-primary">
        {running ? 'Running…' : '▶ Run'}
      </button>
    </div>
  );
}

/* ====================================================================
   Shared: ScrapeBlockedBanner
   FDA's Akamai edge IP-blocks AWS egress, so the Scrape/Fetch stages are
   no-ops in prod until an egress proxy is configured (S-1/S-4 are disabled
   in ingestion_schedule.json for the same reason). Extract/Seed still work
   on already-collected data.
   ==================================================================== */
function ScrapeBlockedBanner() {
  return (
    <div style={{
      background: '#fff8e1', border: '1px solid #f0c36d', color: '#7a5b00',
      padding: '10px 14px', borderRadius: 6, margin: '0 0 12px', fontSize: 13, lineHeight: 1.5,
    }}>
      ⚠️ <strong>Scraping is unavailable from production.</strong> FDA's Akamai edge
      IP-blocks AWS egress (302 → abuse-detection → 404), so <em>Run</em> on this stage
      is a no-op in prod until an egress proxy is configured. The <strong>Extractions</strong> and{' '}
      <strong>Vectors</strong> stages still operate on already-collected data.
    </div>
  );
}

/* ====================================================================
   Shared: RawTextViewer
   ==================================================================== */
function RawTextViewer({ text, onClose }) {
  return (
    <div className="raw-text-overlay" onClick={onClose}>
      <div className="raw-text-panel" onClick={e => e.stopPropagation()}>
        <div className="raw-text-header">
          <span>Raw Text</span>
          <button onClick={onClose}>✕</button>
        </div>
        <pre className="raw-text-content">{text}</pre>
      </div>
    </div>
  );
}

/* ====================================================================
   Shared: ClaimChips
   ==================================================================== */
function ClaimChips({ claims, labels }) {
  const labelMap = {};
  if (Array.isArray(labels)) {
    labels.forEach(l => { if (l.claim) labelMap[l.claim] = l.label; });
  }
  const colorMap = { DRUG: '#f44336', OTC: '#ff9800', COSMETIC: '#4caf50' };
  return (
    <div className="claim-chips">
      {(claims || []).map((c, i) => {
        const lbl = labelMap[c] || 'UNKNOWN';
        return (
          <span key={i} className="claim-chip" style={{ borderColor: colorMap[lbl] || '#999' }}>
            <span className="chip-dot" style={{ background: colorMap[lbl] || '#999' }} />
            {c}
          </span>
        );
      })}
    </div>
  );
}

/* ====================================================================
   Pipeline Dashboard
   ==================================================================== */
function PipelineDashboard({ stats, onRefresh, onQuickRun, running }) {
  const [quickLimit, setQuickLimit] = useState(5);
  return (
    <div className="pipeline-dashboard">
      <div className="pipeline-flow">
        {[
          { label: 'URLs', count: stats.urls, phase: 'discovered' },
          { label: 'Pages', count: stats.pages, phase: 'fetched' },
          { label: 'Extractions', count: stats.extractions, phase: 'extracted' },
          { label: 'Vectors', count: stats.vectors, phase: 'seeded' },
        ].map((p, i) => (
          <React.Fragment key={p.label}>
            {i > 0 && <span className="flow-arrow">→</span>}
            <div className="flow-box">
              <div className="flow-count">{p.count ?? '–'}</div>
              <div className="flow-label">{p.label} {p.phase}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="quick-run">
        <span>Quick Run: process next</span>
        <input type="number" min={1} max={50} value={quickLimit}
          onChange={e => setQuickLimit(Math.max(1, +e.target.value))} />
        <span>through all phases</span>
        <button onClick={() => onQuickRun(quickLimit)} disabled={running} className="btn-primary">
          {running ? 'Running…' : '▶ Go'}
        </button>
        <button onClick={onRefresh} className="btn-secondary">↻ Refresh</button>
      </div>
    </div>
  );
}

/* ====================================================================
   Tab 1: URLs
   ==================================================================== */
function UrlsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [limit, setLimit] = useState(10);
  const [running, setRunning] = useState(false);
  const [urlStats, setUrlStats] = useState({});

  const load = useCallback(async () => {
    try {
      const qs = `page=${page}&per_page=20${filter ? `&status=${filter}` : ''}`;
      const [list, stats] = await Promise.all([
        fetch(`${BASE}/urls?${qs}`).then(r => r.json()),
        fetch(`${BASE}/urls/stats`).then(r => r.json()),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setUrlStats(stats);
    } catch (e) { console.error('Failed to load URLs', e); }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  const runScraper = async () => {
    setRunning(true);
    try {
      await fetch(`${BASE}/urls/scrape?limit=${limit}`, { method: 'POST' });
      await load();
    } finally { setRunning(false); }
  };

  const statusBadge = (s) => {
    const map = { discovered: '🔵', fetched: '✅', failed: '❌' };
    return <span className={`badge badge-${s}`}>{map[s] || '?'} {s}</span>;
  };

  return (
    <div className="tab-content">
      <ScrapeBlockedBanner />
      <div className="stats-bar">
        <span>Total: {urlStats.total || 0}</span>
        <span>Discovered: {urlStats.discovered || 0}</span>
        <span>Fetched: {urlStats.fetched || 0}</span>
        <span>Failed: {urlStats.failed || 0}</span>
      </div>
      <BatchControls label="Scrape FDA index page" limit={limit} setLimit={setLimit}
        onRun={runScraper} running={running} />
      <div className="filter-row">
        <label>Filter:
          <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="discovered">Discovered</option>
            <option value="fetched">Fetched</option>
            <option value="failed">Failed</option>
          </select>
        </label>
      </div>
      <table className="data-table">
        <thead>
          <tr><th>#</th><th>Title</th><th>Company</th><th>Date</th><th>Status</th><th>Discovered</th></tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={r.id}>
              <td>{(page - 1) * 20 + i + 1}</td>
              <td className="text-truncate" title={r.letter_url}>{r.title || r.letter_url?.slice(0, 60)}</td>
              <td>{r.company_name || '–'}</td>
              <td>{r.issue_date || '–'}</td>
              <td>{statusBadge(r.status)}</td>
              <td>{r.discovered_at ? new Date(r.discovered_at).toLocaleDateString() : '–'}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="empty">No URLs discovered yet</td></tr>}
        </tbody>
      </table>
      <Pagination page={page} total={total} perPage={20} setPage={setPage} />
    </div>
  );
}

/* ====================================================================
   Tab 2: Fetched Pages
   ==================================================================== */
function FetchedPagesTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [running, setRunning] = useState(false);
  const [viewText, setViewText] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await fetch(`${BASE}/pages?page=${page}&per_page=20`).then(r => r.json());
      setItems(list.items || []);
      setTotal(list.total || 0);
    } catch (e) { console.error('Failed to load pages', e); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const runFetcher = async () => {
    setRunning(true);
    try {
      await fetch(`${BASE}/pages/fetch?limit=${limit}`, { method: 'POST' });
      await load();
    } finally { setRunning(false); }
  };

  const viewPage = async (id) => {
    const data = await fetch(`${BASE}/pages/${id}`).then(r => r.json());
    setViewText(data.raw_text || data.raw_html || 'No content');
  };

  return (
    <div className="tab-content">
      <ScrapeBlockedBanner />
      <BatchControls label="Fetch unfetched URLs" limit={limit} setLimit={setLimit}
        onRun={runFetcher} running={running} />
      <table className="data-table">
        <thead>
          <tr><th>#</th><th>Title</th><th>Company</th><th>HTTP</th><th>Size</th><th>Duration</th><th>Fetched</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={r.id}>
              <td>{(page - 1) * 20 + i + 1}</td>
              <td className="text-truncate">{r.title || '–'}</td>
              <td>{r.company_name || '–'}</td>
              <td><span className={r.http_status === 200 ? 'badge badge-fetched' : 'badge badge-failed'}>{r.http_status}</span></td>
              <td>{r.text_length ? `${Math.round(r.text_length / 1024)}KB` : '–'}</td>
              <td>{r.fetch_duration_ms ? `${r.fetch_duration_ms}ms` : '–'}</td>
              <td>{r.fetched_at ? new Date(r.fetched_at).toLocaleDateString() : '–'}</td>
              <td><button className="btn-sm" onClick={() => viewPage(r.id)}>View</button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={8} className="empty">No pages fetched yet</td></tr>}
        </tbody>
      </table>
      <Pagination page={page} total={total} perPage={20} setPage={setPage} />
      {viewText && <RawTextViewer text={viewText} onClose={() => setViewText(null)} />}
    </div>
  );
}

/* ====================================================================
   Tab 3: Extractions
   ==================================================================== */
function ExtractionsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [method, setMethod] = useState('regex');
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await fetch(`${BASE}/extractions?page=${page}&per_page=20`).then(r => r.json());
      setItems(list.items || []);
      setTotal(list.total || 0);
    } catch (e) { console.error('Failed to load extractions', e); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const runExtractor = async () => {
    setRunning(true);
    try {
      await fetch(`${BASE}/extractions/extract?limit=${limit}&method=${method}`, { method: 'POST' });
      await load();
    } finally { setRunning(false); }
  };

  const toggleExpand = async (id) => {
    if (expanded?.id === id) { setExpanded(null); return; }
    const detail = await fetch(`${BASE}/extractions/${id}`).then(r => r.json());
    setExpanded(detail);
  };

  return (
    <div className="tab-content">
      <BatchControls label="Extract from pages" limit={limit} setLimit={setLimit}
        onRun={runExtractor} running={running}
        extra={
          <select value={method} onChange={e => setMethod(e.target.value)} className="method-select">
            <option value="regex">Regex</option>
            <option value="llm">LLM</option>
            <option value="hybrid">Hybrid</option>
          </select>
        }
      />
      <table className="data-table">
        <thead>
          <tr><th>#</th><th>Company</th><th>Date</th><th>Ref</th><th>Claims</th><th>Citations</th><th>Method</th><th>Conf.</th></tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <React.Fragment key={r.id}>
              <tr onClick={() => toggleExpand(r.id)} className="clickable-row">
                <td>{(page - 1) * 20 + i + 1}</td>
                <td>{r.company_name || '–'}</td>
                <td>{r.letter_date || '–'}</td>
                <td>{r.reference_number || '–'}</td>
                <td>{r.claims_count || 0}</td>
                <td>{r.citations_count || 0}</td>
                <td>{r.extraction_method}</td>
                <td>{r.extraction_confidence ? `${Math.round(r.extraction_confidence * 100)}%` : '–'}</td>
              </tr>
              {expanded?.id === r.id && (
                <tr><td colSpan={8}>
                  <div className="extraction-detail">
                    <h4>Quoted Claims</h4>
                    <ClaimChips claims={expanded.quoted_claims} labels={expanded.classification_labels} />
                    {expanded.regulatory_citations?.length > 0 && (
                      <>
                        <h4>Regulatory Citations</h4>
                        <ul>{expanded.regulatory_citations.map((c, j) => <li key={j}>{c}</li>)}</ul>
                      </>
                    )}
                    {expanded.new_keywords?.length > 0 && (
                      <>
                        <h4>New Keywords</h4>
                        <div className="new-keywords">
                          {expanded.new_keywords.map((k, j) => <span key={j} className="new-kw">{k}</span>)}
                        </div>
                      </>
                    )}
                    {expanded.required_actions && (
                      <>
                        <h4>Required Actions</h4>
                        <p className="actions-text">{expanded.required_actions}</p>
                      </>
                    )}
                  </div>
                </td></tr>
              )}
            </React.Fragment>
          ))}
          {items.length === 0 && <tr><td colSpan={8} className="empty">No extractions yet</td></tr>}
        </tbody>
      </table>
      <Pagination page={page} total={total} perPage={20} setPage={setPage} />
    </div>
  );
}

/* ====================================================================
   Tab 4: Vectors
   ==================================================================== */
function VectorsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [running, setRunning] = useState(false);
  const [vecStats, setVecStats] = useState({});

  const load = useCallback(async () => {
    try {
      const [list, stats] = await Promise.all([
        fetch(`${BASE}/vectors?page=${page}&per_page=20`).then(r => r.json()),
        fetch(`${BASE}/vectors/stats`).then(r => r.json()),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setVecStats(stats);
    } catch (e) { console.error('Failed to load vectors', e); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const runSeeder = async () => {
    setRunning(true);
    try {
      await fetch(`${BASE}/vectors/seed?limit=${limit}`, { method: 'POST' });
      await load();
    } finally { setRunning(false); }
  };

  const labelBadge = (l) => {
    const colors = { DRUG: '#f44336', OTC: '#ff9800', COSMETIC: '#4caf50' };
    return <span className="badge" style={{ background: colors[l] || '#999', color: '#fff' }}>{l}</span>;
  };

  return (
    <div className="tab-content">
      <div className="stats-bar">
        <span>Total seeded: {vecStats.total || 0}</span>
        <span>Pending: {vecStats.pending_extractions || 0}</span>
        {vecStats.by_label && Object.entries(vecStats.by_label).map(([k, v]) => (
          <span key={k}>{k}: {v}</span>
        ))}
      </div>
      <BatchControls label="Seed extractions" limit={limit} setLimit={setLimit}
        onRun={runSeeder} running={running} />
      <table className="data-table">
        <thead>
          <tr><th>#</th><th>Claim Text</th><th>Label</th><th>Source Letter</th><th>Embedded</th></tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={r.id}>
              <td>{(page - 1) * 20 + i + 1}</td>
              <td className="text-truncate">{r.claim_text}</td>
              <td>{labelBadge(r.label)}</td>
              <td>{r.company_name || r.reference_number || '–'}</td>
              <td>{r.embedded_at ? new Date(r.embedded_at).toLocaleDateString() : '–'}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="empty">No vectors seeded yet</td></tr>}
        </tbody>
      </table>
      <Pagination page={page} total={total} perPage={20} setPage={setPage} />
    </div>
  );
}

/* ====================================================================
   Pagination
   ==================================================================== */
function Pagination({ page, total, perPage, setPage }) {
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return null;
  return (
    <div className="pagination">
      <button disabled={page <= 1} onClick={() => setPage(page - 1)}>◀ Prev</button>
      <span>Page {page} of {pages} ({total} items)</span>
      <button disabled={page >= pages} onClick={() => setPage(page + 1)}>Next ▶</button>
    </div>
  );
}

/* ====================================================================
   Main: WarningLettersPipeline
   ==================================================================== */
function WarningLettersPipeline() {
  const [tab, setTab] = useState('urls');
  const [pipelineStats, setPipelineStats] = useState({});
  const [running, setRunning] = useState(false);

  const loadStats = async () => {
    try {
      const data = await fetch(`${BASE}/pipeline/status`).then(r => r.json());
      setPipelineStats(data);
    } catch (e) { console.error('Failed to load pipeline stats', e); }
  };

  useEffect(() => { loadStats(); }, []);

  const quickRun = async (limit) => {
    setRunning(true);
    try {
      const resp = await fetch(
        `${BASE}/pipeline/run?scrape_limit=${limit}&fetch_limit=${limit}&extract_limit=${limit}&seed_limit=${limit}`,
        { method: 'POST' }
      );
      if (!resp.ok) {
        const err = await resp.text();
        console.error('Pipeline run failed:', err);
      }
      await loadStats();
    } catch (e) {
      console.error('Pipeline run error', e);
    } finally { setRunning(false); }
  };

  const tabs = [
    { key: 'urls', label: 'URLs' },
    { key: 'pages', label: 'Fetched Pages' },
    { key: 'extractions', label: 'Extractions' },
    { key: 'vectors', label: 'Vectors' },
  ];

  return (
    <div className="pipeline-page">
      <PipelineDashboard stats={pipelineStats} onRefresh={loadStats}
        onQuickRun={quickRun} running={running} />
      <div className="tab-bar">
        {tabs.map(t => (
          <button key={t.key} className={tab === t.key ? 'tab active' : 'tab'}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      {tab === 'urls' && <UrlsTab />}
      {tab === 'pages' && <FetchedPagesTab />}
      {tab === 'extractions' && <ExtractionsTab />}
      {tab === 'vectors' && <VectorsTab />}
    </div>
  );
}

export default WarningLettersPipeline;
